import { CENTERCOMPONENTS } from './CenterComponents';
import { ACTIVETAB } from './RightSideComponents';
import { useMapPanelContext } from './MapPanelContext';
import { type CSSProperties, useMemo } from 'react';
import { useSelectionState } from '../../../components/SelectionStateProvider';
import { useViewMode, ViewMode } from '../../../components/ViewModeProvider';
import { computeGridStyles } from './computeGridStyles';

export interface GridClasses {
  layerControllerGrid: CSSProperties;
  followShipGrid: CSSProperties;
  metadataGrid: CSSProperties;
  reportLegendGrid: CSSProperties;
  reportTableGrid: CSSProperties;
  targetInfoGrid: CSSProperties;
  mapPanelGrid: CSSProperties;
  replayGrid: CSSProperties;
  portInfoGrid: CSSProperties;
}

export const useGridClasses = (isTargetCardExpanded: boolean): GridClasses => {
  const {
    activeTab,
    centerComponent,
    tableExpanded,
    reportDetailsExpanded,
    tableHeight,
  } = useMapPanelContext();
  const { targetInfoDetailsList, selectedPorts, portSelectionMode } =
    useSelectionState();
  const { viewMode } = useViewMode();

  // Check if we're in Drawing or Kiosk mode (minimal UI)
  const isDrawingMode =
    viewMode === ViewMode.Drawing || viewMode === ViewMode.Kiosk;

  const isTableExpandedAndActive = useMemo(() => {
    if (!tableExpanded) {
      return false;
    }

    if (centerComponent === CENTERCOMPONENTS.NONE) {
      return false;
    }

    return true;
  }, [tableExpanded, centerComponent]);

  const isReplayActive = useMemo(() => {
    return activeTab === ACTIVETAB.REPLAY;
  }, [activeTab]);

  const isSidebarActive = useMemo(() => {
    if (activeTab === ACTIVETAB.NONE) {
      return false;
    }

    if (activeTab === ACTIVETAB.REPLAY) {
      return false;
    }

    // This is where the report sidebar is active, but hidden
    if (!reportDetailsExpanded) {
      return false;
    }

    return true;
  }, [activeTab, reportDetailsExpanded]);

  const isTargetPanelActive = useMemo(() => {
    if (targetInfoDetailsList.length === 0) {
      return false;
    }

    return true;
  }, [targetInfoDetailsList]);

  const isPortPanelActive = useMemo(() => {
    if (selectedPorts.length === 0) {
      return false;
    }

    // Port panel is only active for 'nextport' and 'popout' modes
    // For 'routes' mode, only the routes table is shown (no side panel)
    if (portSelectionMode === 'routes') {
      return false;
    }

    return true;
  }, [selectedPorts, portSelectionMode]);

  const styles = useMemo(
    () =>
      computeGridStyles({
        tableExpanded: isTableExpandedAndActive,
        sidebarActive: false, // Sidebar uses flex layout outside the grid
        replayActive: isReplayActive,
        targetPanelActive: isTargetPanelActive,
        portPanelActive: isPortPanelActive,
        multipleTargets: targetInfoDetailsList.length > 1,
        multiplePorts: selectedPorts.length > 1,
        targetCardExpanded: isTargetCardExpanded,
        drawingMode: isDrawingMode,
        tableHeight,
      }),
    [
      isTableExpandedAndActive,
      isSidebarActive,
      isReplayActive,
      isTargetPanelActive,
      isPortPanelActive,
      targetInfoDetailsList.length,
      selectedPorts.length,
      isTargetCardExpanded,
      isDrawingMode,
      tableHeight,
    ],
  );

  return {
    layerControllerGrid: styles.layerController,
    followShipGrid: styles.followShip,
    metadataGrid: styles.metadata,
    reportLegendGrid: styles.reportLegend,
    reportTableGrid: styles.reportTable,
    targetInfoGrid: styles.targetInfo,
    mapPanelGrid: styles.mapPanel,
    replayGrid: styles.replay,
    portInfoGrid: styles.portInfo,
  };
};
